import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSettings, FiChevronDown, FiCheck, FiGlobe, FiMapPin, FiAlertCircle, FiTool } from 'react-icons/fi';
import ReactCountryFlag from 'react-country-flag';
import { HeaderSection } from './Layout';

const VERSION = 'Online';

// Styled Components for Header
const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg} 0 calc(${({ theme }) => theme.spacing.xl} - 16px);
  text-align: center;
  width: 100%;
  
  ${({ theme }) => theme.media.tablet} {
    padding: ${({ theme }) => theme.spacing.xl} 0 calc(${({ theme }) => theme.spacing.xxl} - 16px);
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.border.default};
  margin: ${({ theme }) => theme.spacing.sm} 0;
  opacity: 0.2;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
`;


const IPExplainerText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
`;

const ChevronIcon = styled(FiChevronDown)`
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform 0.2s;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled(motion.h1)`
  font-size: 1.85rem;
  font-weight: 800;
  background: linear-gradient(135deg, #FFFFFF 0%, ${({ theme }) => theme.colors.primary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  letter-spacing: -0.02em;
  text-align: center;
  line-height: 1.1;
  
  ${({ theme }) => theme.media.mobileLarge} {
    font-size: 2.25rem;
  }
    
  ${({ theme }) => theme.media.tablet} {
    font-size: 2.75rem;
  }
`;

const HeaderDescription = styled(motion.p)`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin: ${({ theme }) => theme.spacing.md} auto 0;
  line-height: 1.5;
  max-width: 480px;
  font-weight: 400;

  ${({ theme }) => theme.media.mobileLarge} {
    font-size: 1.05rem;
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

const WelcomeText = styled(motion.h3)`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin: 0 0 12px 0;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  font-weight: 500;
  opacity: 0.7;
`;

const PreviewButton = styled(motion.p)`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  display: inline-block;
  margin-left: auto;
  margin-right: auto;
  transform: translateY(-1px);
`;

const actionPillStyles = `
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: 40px;
  padding: 4px;
  padding-right: 12px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  backdrop-filter: blur(8px);

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    transform: translateY(0);
  }
`;

const SettingsPill = styled(motion.div)`
  ${actionPillStyles}
  gap: 8px;
`;

const PillIconCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  flex-shrink: 0;
`;

const PillText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: none;
  
  ${({ theme }) => theme.media.tablet} {
    display: block;
  }
`;

const LanguagePill = styled(motion.div)`
  ${actionPillStyles}
  padding: 6px 12px;
  gap: 8px;
  justify-content: center;
  min-width: 0;
`;

const FlagContainer = styled.div`
  width: 24px;
  height: 18px;
  border-radius: 2px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
`;

const LanguageDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: 12px;
  padding: 8px;
  min-width: 180px;
  box-shadow: ${({ theme }) => theme.shadows.prominent};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1000;
  backdrop-filter: blur(12px);
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s;
  color: ${({ theme, $disabled }) => $disabled ? theme.colors.text.muted : theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 600;
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};

  &:hover {
    background: ${({ $disabled }) => $disabled ? 'transparent' : 'rgba(255, 255, 255, 0.05)'};
    color: ${({ theme, $disabled }) => $disabled ? theme.colors.text.muted : theme.colors.text.primary};
  }

  .check-icon {
    margin-left: auto;
    color: ${({ theme }) => theme.colors.active};
  }
`;








export const VisaFormHeader = ({ location, actions, language = 'en', setLanguage }) => {
  const [isLangOpen, setIsLangOpen] = React.useState(false);
  const langDropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };

    if (isLangOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isLangOpen]);

  // Helper for language handling
  const handleLanguageChange = (lang) => {
    if (setLanguage) {
      setLanguage(lang);
    }
    setIsLangOpen(false);
  };

  return (
    <>
      <HeaderSection
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <HeaderContainer>
          <TopBar>
            <ActionsContainer>
              {/* Settings Pill */}
              <SettingsPill
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={actions.handleOpenPreferences}
              >
                <PillIconCircle>
                  <FiSettings size={18} />
                </PillIconCircle>
                <FiChevronDown size={14} style={{ marginLeft: '4px', opacity: 0.7 }} />
              </SettingsPill>

              {/* Language Selector Pill */}
              <div style={{ position: 'relative' }} ref={langDropdownRef}>
                <LanguagePill
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsLangOpen(!isLangOpen)}
                >
                  <FlagContainer>
                    <ReactCountryFlag
                      countryCode={language === 'en' ? 'GB' : language === 'fr' ? 'FR' : 'GB'}
                      svg
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </FlagContainer>
                  <ChevronIcon $isOpen={isLangOpen} size={14} />
                </LanguagePill>

                <AnimatePresence>
                  {isLangOpen && (
                    <LanguageDropdown
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <DropdownItem onClick={() => handleLanguageChange('en')}>
                        <FlagContainer><ReactCountryFlag countryCode="GB" svg style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></FlagContainer>
                        ENGLISH
                        {language === 'en' && <FiCheck className="check-icon" />}
                      </DropdownItem>
                      <DropdownItem $disabled onClick={(e) => e.preventDefault()}>
                        <FlagContainer><ReactCountryFlag countryCode="FR" svg style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></FlagContainer>
                        FRANÇAIS
                        <FiTool className="check-icon" />
                      </DropdownItem>
                    </LanguageDropdown>
                  )}
                </AnimatePresence>
              </div>
            </ActionsContainer>
          </TopBar>

          <Divider />

          <HeroContent>
            <WelcomeText
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              WELCOME TO
            </WelcomeText>

            <HeaderTitle>
              VISA Checker
            </HeaderTitle>

            <PreviewButton
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {VERSION}
            </PreviewButton>

            <HeaderDescription
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Get instant visa validation with comprehensive reports and real-time countdowns.
            </HeaderDescription>
          </HeroContent>

        </HeaderContainer>
      </HeaderSection>
    </>
  );
};

export default VisaFormHeader;
